import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AppConfigInterface,
  EndPointsInterface,
} from '@app/models/app-config.interface';
import { Constants } from '@app/models/constants.class';
import { EntityInterface } from '@app/models/entity.interface';
import { ConfigService } from '@ng-darwin/config';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * @description Injectable
 * @class CategorizerService
 */
@Injectable({
  providedIn: 'root',
})
export class CategorizerService {
  private readonly _appConfig: AppConfigInterface = {};
  private readonly _endPoints: EndPointsInterface = {};
  private _baseURL = '';
  private _putEntitiesInFlight: Promise<HttpResponse<any>> | null = null;
  public ownershipValidation = false;
  public isProcessFinished = false;
  /**
   * @description Creates an instance of CategorizerService.
   * @param _http HttpClient
   * @param _configService ConfigService
   */
  constructor(
    private readonly _http: HttpClient,
    private readonly _configService: ConfigService,
  ) {
    this._appConfig = this._configService.config.app || {};
    this._endPoints = this._appConfig.endPoints || {};
    this._baseURL = (
      sessionStorage.getItem('app-unique-id') ===
      this._configService.config.app['cdncApplication']
        ? this._appConfig.baseUrlCDNC
        : this._appConfig.baseUrl
    ) as string;
  }

  /**
   * @description getEntities
   * @param expedientCode string
   * @returns Promise<EntityInterface[]>
   */
  getEntities(expedientCode: string, onlySantanderFlag: boolean): Promise<EntityInterface[]> {
    // HABILITAR PARA DEV SI NO VA EL SERVICIO
    // const url = 'assets/dataExample/jsonAgreggatedEntities.example.json';
    const url = `${this._baseURL}/${this._endPoints.categorizer}/${expedientCode}?onlySantander=${onlySantanderFlag}`;
    return lastValueFrom(
      this._http.get<EntityInterface>(url).pipe(
        map((values) => {
          const dataMap = new Map(Object.entries(values));
          this.ownershipValidation = dataMap.get('ownershipValidation');
          dataMap.delete('ownershipValidation');
          return this._extractEntitiesFromMap(dataMap);
        }),
      ),
    );
  }

  private _extractEntitiesFromMap(dataMap: Map<string, EntityInterface[]>): EntityInterface[] {
    let entities: EntityInterface[] = [];
    if (dataMap.size > 0) {
      dataMap.forEach((data: EntityInterface[]) => {
        entities = data;
        entities.forEach((entity) => this._assignEntityImage(entity));
      });
    }
    return entities;
  }

  private _assignEntityImage(entity: EntityInterface): void {
    const testName = (entity.name || '').replace(/\s/g, '');
    const matchedBank = Constants.banksWithImages.find(
      (b) => b.toLowerCase() === testName.toLowerCase(),
    );
    entity.image = matchedBank ? `assets/banks/${matchedBank}.svg` : undefined;
  }

  /**
   * @description putEntities
   * @param expedientCode string
   * @returns Promise<HttpResponse<any>>
   */
  putEntities(expedientCode: string): Promise<HttpResponse<any>> {
    if (this._putEntitiesInFlight) {
      return this._putEntitiesInFlight;
    }
    const url = `${this._baseURL}/${this._endPoints.categorizer}/${expedientCode}`;
    this._putEntitiesInFlight = lastValueFrom(
      this._http.put<any>(url, expedientCode, { observe: 'response' }),
    ).finally(() => {
      this._putEntitiesInFlight = null;
    });
    return this._putEntitiesInFlight;
  }

  /**
   * @description putEntities
   * @param processId string
   * @returns Promise<HttpResponse<any>>
   */
  getProcessStatus(processId: string): Promise<HttpResponse<any>> {
    const url = `${this._baseURL}/${this._endPoints.processStatus}/${processId}`;
    return lastValueFrom(this._http.get<any>(url, { observe: 'response' }));
  }

  /**
   * @description deleteBank
   * Deletes an aggregated bank for the current expedient.
   * Uses v2 endpoint (aggregations) for CDNC/soft-login customers
   * and v1 endpoint (categorizer) for retail/business customers.
   * @param expedientCode string
   * @param entityCode string
   * @returns Promise<void>
   */
  deleteBank(expedientCode: string, entityCode: string): Promise<void> {
    const isCDNC = sessionStorage.getItem('app-unique-id') === this._configService.config.app['cdncApplication'];
    const url = isCDNC
      ? `${this._appConfig.baseUrlCDNC}/${this._endPoints.deleteAggregation}/${expedientCode}/banks/${entityCode}`
      : `${this._baseURL}/${this._endPoints.deleteCategorizer}/${expedientCode}/banks/${entityCode}`;
    return lastValueFrom(this._http.delete<void>(url));
  }
}
